import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { C, v, GPS_SCHOOLS, ME_SCHOOLS, computeDerived, ESA_NAMES } from "./schoolData";

const CONF_STYLE = {
  V: { bg: "transparent", text: "inherit", dot: "#22863a", label: "Verified" },
  E: { bg: "#fffbf0",     text: "#7a5c00", dot: "#d4a017", label: "Estimated" },
  L: { bg: "#fff5f5",     text: "#8b2c2c", dot: "#c0392b", label: "Low confidence" },
};

const CONF_TOOLTIP = {
  V: "Verified — sourced directly from the school website, Wikipedia citing IRS Form 990 filings, or a published financial disclosure. Safe to present.",
  E: "Estimated — derived from aggregator sites, secondary sources, or calculated from other verified figures. Cross-check before presenting.",
  L: "Low confidence — order-of-magnitude only. Not found in any primary source. Do not cite without independent verification.",
};

// ─── CHART DESCRIPTIONS ──────────────────────────────────────────────────────
const CHART_DESCRIPTIONS = {
  tuition: "Annual all-inclusive boarding tuition in USD for the 2024–25 academic year where verified, otherwise most recently published figure. This is the sticker price before financial aid. Schools at the lower end (e.g. Groton, King's) maintain deliberate pricing policies; those at the upper end (e.g. Webb, Blair, Berkshire) reflect higher cost structures or less price restraint. Lower tuition does not necessarily indicate lower quality — Groton and King's are among the most selective schools in their respective peer groups.",
  aid: "Percentage of the total student body receiving need-based financial aid grants. A higher figure indicates broader socioeconomic access. Schools above 40% (Andover, Exeter, Deerfield, King's) have made access a defining institutional priority, typically backed by large endowments or — in King's case — a strong operating commitment. This metric does not capture average grant size, which can vary significantly.",
  ratio: "Estimated gross tuition revenue divided by total annual financial aid disbursed. A ratio of 3x means the school collects roughly $3 in tuition for every $1 it gives back in aid. Lower is more generous relative to revenue. Schools below 3x (Exeter, Andover, NMH, Groton) are recycling an exceptionally high share of tuition income into aid — typically because large endowments supplement operating revenue. King's Academy at 3.7x is notably generous given the absence of a published endowment, meaning its aid program is funded almost entirely from tuition cashflow.",
  boarding: "Percentage of the enrolled student body living on campus as full boarders. Schools at 100% (Episcopal HS, St. Paul's, St. Andrew's DE) are fully residential with no day students. King's Academy at 55% reflects its dual role serving both the local Jordanian day student market and an international boarding population — a different structural model from the classic New England all-boarding school.",
  sustainability: "Financial aid sustainability score: annual aid budget as a percentage of total endowment. Lower is more sustainable — a score of 3% means the school could theoretically fund its current aid program indefinitely from endowment returns alone (assuming a standard 5% payout). Scores above 10% indicate the school is drawing heavily on operating revenue to fund aid, making the program vulnerable to enrollment fluctuations or economic downturns. Schools with no published endowment (including King's Academy) are shown separately — their aid programs have no endowment backstop and are entirely dependent on tuition revenue.",
  dayTuition: "Annual day school tuition in USD. UAE and Qatar fees are government-regulated (KHDA/ADEK) and among the most reliably published in the region. Saudi and Kuwait figures involve estimation. Egyptian EGP figures are particularly unreliable in USD terms due to recent sharp currency depreciation — treat Egypt school day tuition data with caution. King's day tuition ($42,000) is used for comparison in this view.",
};

// ─── UI HELPERS ───────────────────────────────────────────────────────────────
function ConfDot({ conf }) {
  const s = CONF_STYLE[conf];
  return (
    <span title={s.label} style={{ display: "inline-block", width: 6, height: 6, borderRadius: "50%", background: s.dot, marginLeft: 4, verticalAlign: "middle", flexShrink: 0 }} />
  );
}

function Cell({ field }) {
  if (!field) return <td style={{ color: "#aaa", fontStyle: "italic", fontSize: 11 }}>—</td>;
  const { val, conf } = field;
  const s = CONF_STYLE[conf];
  return (
    <td style={{ background: s.bg, color: s.text, textAlign: "right", whiteSpace: "nowrap", fontFamily: "var(--font-data)", fontSize: 12, fontWeight: 500 }}>
      {val !== null ? (
        <span style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 2 }}>
          <span>{val}</span>
          {conf !== C.V && <ConfDot conf={conf} />}
        </span>
      ) : <span style={{ color: "#bbb", fontStyle: "italic" }}>N/A</span>}
    </td>
  );
}

function MoneyCell({ field, prefix = "$", suffix = "" }) {
  if (!field) return <td style={{ color: "#aaa", fontSize: 11 }}>—</td>;
  const { val, conf } = field;
  const s = CONF_STYLE[conf];
  const display = val !== null ? `${prefix}${val.toLocaleString()}${suffix}` : null;
  return (
    <td style={{ background: s.bg, color: s.text, textAlign: "right", whiteSpace: "nowrap", fontFamily: "var(--font-data)", fontSize: 12, fontWeight: 500 }}>
      {display ? (
        <span style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 2 }}>
          <span>{display}</span>
          {conf !== C.V && <ConfDot conf={conf} />}
        </span>
      ) : <span style={{ color: "#bbb", fontStyle: "italic" }}>N/A</span>}
    </td>
  );
}

function MiniBar({ pct, color = "#3d3830" }) {
  if (pct === null || pct === undefined) return <span style={{ color: "#bbb", fontSize: 11 }}>—</span>;
  const w = Math.max(2, Math.round(pct * 0.45));
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
      <div style={{ width: w, height: 5, borderRadius: 2, background: color, opacity: 0.65, flexShrink: 0 }} />
      <span style={{ fontFamily: "var(--font-data)", fontSize: 11, color: "#7a7068", whiteSpace: "nowrap" }}>{pct}%</span>
    </div>
  );
}

function RatioCell({ ratio, conf }) {
  if (ratio === null) return <td style={{ color: "#bbb", fontSize: 11, textAlign: "right" }}>—</td>;
  const color = ratio < 3.5 ? "#22863a" : ratio < 6 ? "#7a5c00" : "#8b2c2c";
  const s = CONF_STYLE[conf];
  return (
    <td style={{ background: s.bg, textAlign: "right", whiteSpace: "nowrap" }}>
      <span style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 2 }}>
        <span style={{ color, fontWeight: 600, fontFamily: "var(--font-data)", fontSize: 12 }}>{ratio.toFixed(1)}x</span>
        {conf !== C.V && <ConfDot conf={conf} />}
      </span>
    </td>
  );
}

function HorzBar({ label, value, maxVal, color, isKings, unit = "", suffix = "" }) {
  const pct = Math.round((value / maxVal) * 100);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
      <div style={{ width: 130, fontSize: 11, fontFamily: "var(--font-data)", color: isKings ? "#1a3a5c" : "#3d3830", fontWeight: isKings ? 600 : 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flexShrink: 0 }}>
        {label}
      </div>
      <div style={{ flex: 1, background: "#e8e2d8", borderRadius: 1, height: 16, position: "relative" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 1, opacity: isKings ? 1 : 0.65 }} />
      </div>
      <div style={{ width: 65, fontSize: 11, fontFamily: "var(--font-data)", fontWeight: 600, color: isKings ? "#1a3a5c" : "#3d3830", textAlign: "right", flexShrink: 0 }}>
        {unit}{value.toLocaleString()}{suffix}
      </div>
    </div>
  );
}

function JordanFlag({ size = 60 }) {
  const h = size * 2 / 3;
  return (
    <svg viewBox="0 0 180 120" width={size} height={h} style={{ borderRadius: 2, boxShadow: "0 1px 4px rgba(0,0,0,0.35)", flexShrink: 0 }}>
      <rect x="0" y="0" width="180" height="40" fill="#000000" />
      <rect x="0" y="40" width="180" height="40" fill="#FFFFFF" />
      <rect x="0" y="80" width="180" height="40" fill="#007A3D" />
      <polygon points="0,0 90,60 0,120" fill="#CE1126" />
      <polygon points="38.00,46.00 40.60,54.59 48.95,51.27 43.85,58.66 51.65,63.12 42.69,63.74 44.07,72.61 38.00,66.00 31.93,72.61 33.31,63.74 24.35,63.12 32.15,58.66 27.05,51.27 35.40,54.59" fill="#FFFFFF" />
    </svg>
  );
}

function LegendItem({ confKey, style: s }) {
  const [show, setShow] = useState(false);
  return (
    <span onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}
      style={{ display: "flex", alignItems: "center", gap: 5, fontFamily: "'Rajdhani','Trebuchet MS',sans-serif", fontSize: 11, color: "#3d3830", cursor: "help", position: "relative" }}>
      <span style={{ width: 8, height: 8, borderRadius: "50%", background: s.dot, display: "inline-block" }} />
      {s.label}
      {confKey !== "V" && <span style={{ background: s.bg === "transparent" ? "#f4f0e8" : s.bg, color: s.text, padding: "1px 5px", borderRadius: 2, fontSize: 10, fontWeight: 600 }}>{confKey}</span>}
      {show && (
        <span style={{ position: "absolute", bottom: "calc(100% + 6px)", left: 0, background: "#1a1714", color: "#faf7f2", fontSize: 11, fontFamily: "'Rajdhani','Trebuchet MS',sans-serif", lineHeight: 1.5, padding: "8px 11px", borderRadius: 2, width: 280, zIndex: 9999, pointerEvents: "none", boxShadow: "0 4px 16px rgba(0,0,0,0.35)", whiteSpace: "normal" }}>
          <span style={{ display: "block", fontWeight: 600, color: s.dot, marginBottom: 3 }}>
            {confKey === "V" ? "✓ Verified" : confKey === "E" ? "~ Estimated" : "⚠ Low Confidence"}
          </span>
          {CONF_TOOLTIP[confKey]}
        </span>
      )}
    </span>
  );
}

// ─── CHART MODAL ─────────────────────────────────────────────────────────────
// filterOptions: array of {key, label} to show as subset buttons. Pass [] or omit to hide.
// "all" key = show everything; "esa" = ESA + King's; "gps" = GPS only; "me" = ME + King's
function ChartModal({ title, description, data, maxVal, colorFn, unit, suffix, noEndowmentSchools, filterOptions, onClose }) {
  const [subset, setSubset] = useState("all");

  const applySubset = (d) => {
    if (subset === "esa") return d.filter(r => r.e || r.k);
    if (subset === "gps") return d.filter(r => !r.m);
    if (subset === "me")  return d.filter(r => r.m || r.k);
    return d;
  };

  const visible = applySubset(data);
  const sorted  = [...visible].sort((a, b) => a.v - b.v);
  const visMax  = Math.max(...sorted.map(x => x.v));
  const visibleNoEndow = noEndowmentSchools
    ? applySubset(noEndowmentSchools.map(n => ({ n, k: n === "King's Academy", m: false, e: ESA_NAMES.has(n) }))).map(r => r.n)
    : null;

  const subsetLabel = subset === "esa" ? "ESA schools + King's Academy"
    : subset === "gps" ? "GPS schools + King's Academy"
    : subset === "me"  ? "Middle East schools + King's Academy"
    : "All schools";

  const filters = filterOptions && filterOptions.length > 0 ? filterOptions : null;

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#faf7f2", borderRadius: 3, boxShadow: "0 8px 40px rgba(0,0,0,0.4)", width: "min(820px,95vw)", maxHeight: "90vh", overflowY: "auto", padding: "28px 28px 24px" }}>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16, borderBottom: "2px solid #1a1714", paddingBottom: 12 }}>
          <div>
            <div style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: "1.2rem", fontWeight: 600 }}>{title}</div>
            <div style={{ fontFamily: "'Rajdhani','Trebuchet MS',sans-serif", fontSize: 11, color: "#7a7068", marginTop: 3 }}>
              {subsetLabel} — sorted ascending · <span style={{ color: "#1a3a5c", fontWeight: 600 }}>★ = King's Academy</span>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0, marginLeft: 16 }}>
            {filters && filters.map(f => (
              <button key={f.key} onClick={e => { e.stopPropagation(); setSubset(f.key); }}
                style={{ padding: "4px 10px", border: `1px solid ${subset === f.key ? "#1a3a5c" : "#c8bfb0"}`, borderRadius: 2, background: subset === f.key ? "#1a3a5c" : "#faf7f2", color: subset === f.key ? "#fff" : "#3d3830", cursor: "pointer", fontFamily: "'Rajdhani','Trebuchet MS',sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: "0.06em", whiteSpace: "nowrap", transition: "all 0.15s" }}>
                {f.label}
              </button>
            ))}
            <button onClick={onClose} style={{ background: "none", border: "1px solid #c8bfb0", borderRadius: 2, padding: "4px 12px", cursor: "pointer", fontFamily: "'Rajdhani','Trebuchet MS',sans-serif", fontSize: 12, color: "#3d3830" }}>✕ Close</button>
          </div>
        </div>

        {/* ESA info banner */}
        {subset === "esa" && (
          <div style={{ background: "#e8f0f8", border: "1px solid #1a3a5c", borderRadius: 2, padding: "8px 12px", marginBottom: 14, fontFamily: "'Rajdhani','Trebuchet MS',sans-serif", fontSize: 11, color: "#1a3a5c", lineHeight: 1.5 }}>
            <strong>Eight Schools Association (ESA):</strong> Andover, Exeter, Choate, Deerfield, Hotchkiss, Lawrenceville, NMH, St. Paul's. All eight commit to meeting 100% of demonstrated financial need.
          </div>
        )}

        {/* Description */}
        {description && (
          <div style={{ background: "#f4f0e8", border: "1px solid #e8e2d8", borderRadius: 2, padding: "12px 14px", marginBottom: 20, fontFamily: "'EB Garamond',Georgia,serif", fontSize: 13, color: "#3d3830", lineHeight: 1.65, fontStyle: "italic" }}>
            {description}
          </div>
        )}

        {/* Bars */}
        {sorted.map(r => (
          <div key={r.n} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
            <div style={{ width: 175, fontSize: 11, fontFamily: "'Rajdhani','Trebuchet MS',sans-serif", color: r.k ? "#1a3a5c" : r.m ? "#5c3a1a" : "#3d3830", fontWeight: r.k ? 700 : 400, flexShrink: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {r.k ? "★ " : ""}
              {r.m && !r.k && <span style={{ marginRight: 4, fontSize: 9, fontWeight: 700, letterSpacing: "0.1em", background: "#5c3a1a", color: "#fdf0e0", padding: "1px 4px", borderRadius: 1, verticalAlign: "middle" }}>ME</span>}
              {r.e && !r.k && <span style={{ marginRight: 4, fontSize: 9, fontWeight: 700, letterSpacing: "0.1em", background: "#3d3830", color: "#fff", padding: "1px 4px", borderRadius: 1, verticalAlign: "middle" }}>ESA</span>}
              {r.n}
            </div>
            <div style={{ flex: 1, background: "#e8e2d8", borderRadius: 1, height: 18, position: "relative" }}>
              <div style={{ width: `${Math.round((r.v / visMax) * 100)}%`, height: "100%", borderRadius: 1, background: colorFn(r), opacity: r.k ? 1 : 0.65 }} />
            </div>
            <div style={{ width: 70, fontSize: 11, fontFamily: "'Rajdhani','Trebuchet MS',sans-serif", fontWeight: 600, color: r.k ? "#1a3a5c" : "#3d3830", textAlign: "right", flexShrink: 0 }}>
              {unit || ""}{typeof r.v === "number" && r.v % 1 !== 0 ? r.v.toFixed(1) : r.v.toLocaleString()}{suffix || ""}
            </div>
          </div>
        ))}

        {/* No-endowment section (sustainability chart) */}
        {visibleNoEndow && visibleNoEndow.length > 0 && (
          <div style={{ marginTop: 18, paddingTop: 14, borderTop: "1px solid #e8e2d8" }}>
            <div style={{ fontFamily: "'Rajdhani','Trebuchet MS',sans-serif", fontSize: 10, fontWeight: 600, letterSpacing: "0.15em", textTransform: "uppercase", color: "#8b2c2c", marginBottom: 8 }}>
              ⚠ No published endowment — aid funded entirely from operating revenue
            </div>
            {visibleNoEndow.map(n => (
              <div key={n} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                <div style={{ width: 175, fontSize: 11, fontFamily: "'Rajdhani','Trebuchet MS',sans-serif", color: n === "King's Academy" ? "#1a3a5c" : "#8b2c2c", fontWeight: n === "King's Academy" ? 700 : 400, flexShrink: 0 }}>
                  {n === "King's Academy" ? "★ " : ""}{n}
                </div>
                <div style={{ flex: 1, background: "#fde8e8", borderRadius: 1, height: 18, display: "flex", alignItems: "center", paddingLeft: 8 }}>
                  <span style={{ fontFamily: "'Rajdhani','Trebuchet MS',sans-serif", fontSize: 10, color: "#8b2c2c", fontWeight: 600 }}>Endowment not published — sustainability unquantifiable</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── CLICKABLE CHART CARD ─────────────────────────────────────────────────────
function ChartCard({ title, children, onExpand }) {
  const [hov, setHov] = useState(false);
  return (
    <div onClick={onExpand} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ background: "#faf7f2", border: `1px solid ${hov ? "#7a7068" : "#e8e2d8"}`, borderRadius: 2, padding: "16px 18px", cursor: "pointer", transition: "border-color 0.15s, box-shadow 0.15s", boxShadow: hov ? "0 2px 12px rgba(0,0,0,0.1)" : "none" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #e8e2d8", paddingBottom: 8, marginBottom: 14 }}>
        <div style={{ fontFamily: "var(--font-data)", fontSize: 10, fontWeight: 600, letterSpacing: "0.18em", textTransform: "uppercase", color: "#7a7068" }}>{title}</div>
        <div style={{ fontFamily: "var(--font-data)", fontSize: 9, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: hov ? "#1a3a5c" : "#c8bfb0", transition: "color 0.15s" }}>Click to expand ↗</div>
      </div>
      {children}
    </div>
  );
}


// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────
export default function GatewayPeerBenchmark() {
  // "gps" | "me" | "all"
  const [datasetMode, setDatasetMode] = useState("gps");
  const [search, setSearch]           = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [genderFilter, setGenderFilter] = useState("");
  const [sortKey, setSortKey]         = useState("name");
  const [sortDir, setSortDir]         = useState(1);
  const [esaOnly, setEsaOnly]         = useState(false);
  const [allSubset, setAllSubset]     = useState("all"); // "all" | "gps" | "me" | "esa" — only active in "all" mode
  const [modal, setModal]             = useState(null);

  // Active school set — King's only appears once in combined mode
  const allSchoolsCombined = useMemo(() =>
    computeDerived([...GPS_SCHOOLS, ...ME_SCHOOLS.filter(s => !s.isKings)]),
  []);

  const activeSchools = useMemo(() => {
    if (datasetMode === "gps") return computeDerived(GPS_SCHOOLS);
    if (datasetMode === "me")  return computeDerived(ME_SCHOOLS);
    // "all" mode — apply subset filter, always include King's
    const base = allSchoolsCombined;
    if (allSubset === "gps") return base.filter(s => !s.isME);
    if (allSubset === "me")  return base.filter(s => s.isME || s.isKings);
    if (allSubset === "esa") return base.filter(s => ESA_NAMES.has(s.name) || s.isKings);
    return base;
  }, [datasetMode]);

  const states = useMemo(() => [...new Set(activeSchools.map(s => s.state))].sort(), [activeSchools]);

  const filtered = useMemo(() => {
    let d = activeSchools.filter(s => {
      if (esaOnly && !s.isKings && !ESA_NAMES.has(s.name)) return false;
      const txt = [s.name, s.location, s.notes, s.grades, s.gender, s.state].join(" ").toLowerCase();
      if (search && !txt.includes(search.toLowerCase())) return false;
      if (stateFilter && s.state !== stateFilter) return false;
      if (genderFilter && s.gender !== genderFilter) return false;
      return true;
    });
    d.sort((a, b) => {
      const numFields = ["boardingT","dayT","aidPct","aidBudgetM","endowmentM","boardingPct","enrollment","ratio","founded"];
      if (numFields.includes(sortKey)) {
        const av = (sortKey === "ratio" ? a.ratio : sortKey === "enrollment" ? a.enrollment.val : sortKey === "boardingPct" ? a.boardingPct.val : a[sortKey]?.val) ?? -Infinity;
        const bv = (sortKey === "ratio" ? b.ratio : sortKey === "enrollment" ? b.enrollment.val : sortKey === "boardingPct" ? b.boardingPct.val : b[sortKey]?.val) ?? -Infinity;
        return sortDir * (av - bv);
      }
      return sortDir * String(a[sortKey] ?? "").localeCompare(String(b[sortKey] ?? ""));
    });
    return d;
  }, [activeSchools, search, stateFilter, genderFilter, sortKey, sortDir, esaOnly]);

  function colSort(k) {
    if (sortKey === k) setSortDir(d => d * -1);
    else { setSortKey(k); setSortDir(-1); }
  }

  const shortName = n => n.replace(/^The /, "").replace(/ School$/, "").replace(/ Academy$/, "");

  // Color functions — ME schools get warm brown, King's gets navy, GPS gets neutral
  const tuitionColor  = r => r.k ? "#1a3a5c" : r.m ? "#7a4f1a" : "#3d3830";
  const aidColor      = r => r.k ? "#1a3a5c" : "#8b2c2c";
  const ratioColor    = r => r.k ? "#1a3a5c" : r.v < 3.5 ? "#22863a" : r.v < 5 ? "#b8924a" : "#8b2c2c";
  const boardingColor = r => r.k ? "#1a3a5c" : "#3d3830";
  const sustainColor  = r => r.k ? "#1a3a5c" : r.v < 5 ? "#22863a" : r.v < 12 ? "#b8924a" : "#8b2c2c";
  const dayTColor     = r => r.k ? "#1a3a5c" : r.m ? "#7a4f1a" : "#3d3830";

  // Full chart datasets — flag ESA and ME schools for modal badges
  const allTuition  = activeSchools.filter(s => s.boardingT.val).map(s => ({ n: shortName(s.name), v: s.boardingT.val, k: s.isKings, e: ESA_NAMES.has(s.name), m: !!s.isME }));
  const allAid      = activeSchools.filter(s => s.aidPct.val).map(s => ({ n: shortName(s.name), v: s.aidPct.val, k: s.isKings, e: ESA_NAMES.has(s.name), m: !!s.isME }));
  const allRatio    = activeSchools.filter(s => s.ratio).map(s => ({ n: shortName(s.name), v: s.ratio, k: s.isKings, e: ESA_NAMES.has(s.name), m: !!s.isME }));
  const allBoarding = activeSchools.filter(s => s.boardingPct.val !== null).map(s => ({ n: shortName(s.name), v: s.boardingPct.val, k: s.isKings, e: ESA_NAMES.has(s.name), m: !!s.isME }));

  // Day tuition — always inject King's as comparison anchor regardless of mode
  const kingsEntry = computeDerived(GPS_SCHOOLS).find(s => s.isKings);
  const allDayT = [
    ...(activeSchools.some(s => s.isKings) ? [] : kingsEntry ? [kingsEntry] : []),
    ...activeSchools.filter(s => s.dayT.val && !s.isKings),
    ...(activeSchools.some(s => s.isKings) ? activeSchools.filter(s => s.isKings && s.dayT.val) : []),
  ].map(s => ({ n: shortName(s.name), v: s.dayT.val, k: s.isKings, e: ESA_NAMES.has(s.name), m: !!s.isME }))
   .filter(r => r.v); // remove any nulls

  const allSustain  = activeSchools.filter(s => s.endowmentM.val && s.aidBudgetM.val).map(s => ({ n: shortName(s.name), v: Math.round((s.aidBudgetM.val / s.endowmentM.val) * 1000) / 10, k: s.isKings, e: ESA_NAMES.has(s.name), m: !!s.isME }));
  const noEndowmentSchools = activeSchools.filter(s => !s.endowmentM.val && s.aidBudgetM.val).map(s => s.name);

  // Modal filter buttons — contextual to the current dataset mode
  const gpsModalFilters = [
    { key: "all", label: "All" },
    { key: "esa", label: "ESA Only" },
  ];
  const allModalFilters = [
    { key: "all", label: "All" },
    { key: "gps", label: "GPS Only" },
    { key: "me",  label: "ME Only" },
    { key: "esa", label: "ESA Only" },
  ];
  const modalFilters = datasetMode === "all" ? allModalFilters
    : datasetMode === "gps" ? gpsModalFilters
    : []; // ME mode — no filter buttons needed

  // Preview data (up to 10 entries for small chart cards)
  const chartTuitionPreview  = [...allTuition].sort((a,b)=>a.v-b.v).filter(r => r.k || !r.m).slice(0,10);
  const chartAidPreview      = [...allAid].sort((a,b)=>a.v-b.v).slice(-10).reverse().slice(0,10).reverse();
  const chartRatioPreview    = [...allRatio].sort((a,b)=>a.v-b.v).slice(0,10);
  const chartBoardingPreview = [...allBoarding].sort((a,b)=>a.v-b.v).slice(0,10);
  const chartDayPreview      = [...allDayT].sort((a,b)=>a.v-b.v).slice(0,10);
  const chartSustainPreview  = [...allSustain].sort((a,b)=>a.v-b.v).slice(0,10);

  const fontData    = "'Rajdhani', 'Trebuchet MS', sans-serif";
  const fontDisplay = "'Playfair Display', Georgia, serif";
  const fontBody    = "'EB Garamond', Georgia, serif";
  const isMEMode    = datasetMode === "me" || datasetMode === "all";

  const Th = ({ k, children }) => (
    <th onClick={() => colSort(k)} style={{ fontFamily: "var(--font-data)", fontSize: 10, fontWeight: 600, letterSpacing: "0.15em", textTransform: "uppercase", padding: "8px 8px", textAlign: k === "name" ? "left" : "right", background: "#1a1714", color: sortKey === k ? "#d4ac6a" : "rgba(250,247,242,0.65)", cursor: "pointer", whiteSpace: "nowrap", borderRight: "1px solid rgba(255,255,255,0.05)", userSelect: "none" }}>
      {children} {sortKey === k ? (sortDir === 1 ? "↑" : "↓") : <span style={{ opacity: 0.3 }}>↕</span>}
    </th>
  );

  return (
    <div style={{ fontFamily: fontBody, fontSize: 15, color: "#1a1714", background: "#faf7f2", minHeight: "100vh" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,600;0,700;1,400&family=EB+Garamond:ital,wght@0,400;0,500;1,400&family=Rajdhani:wght@400;500;600&display=swap');
        :root { --font-data: ${fontData}; }
        * { box-sizing: border-box; }
        table { border-collapse: collapse; width: 100%; }
        tbody tr:hover { background: #f0ebe0 !important; }
        input, select, button { font-family: ${fontData}; }
        ::-webkit-scrollbar { height: 6px; width: 6px; }
        ::-webkit-scrollbar-track { background: #e8e2d8; }
        ::-webkit-scrollbar-thumb { background: #7a7068; border-radius: 3px; }
      `}</style>

      {/* ── CHART MODAL ── */}
      {modal && (
        <ChartModal
          title={modal.title} description={modal.description} data={modal.data}
          maxVal={modal.maxVal} colorFn={modal.colorFn} unit={modal.unit}
          suffix={modal.suffix} noEndowmentSchools={modal.noEndowmentSchools}
          onClose={() => setModal(null)}
        />
      )}

      {/* ── MASTHEAD ── */}
      <header style={{ background: "#1a1714", color: "#faf7f2", textAlign: "center", padding: "2.5rem 2rem 2rem" }}>
        <div style={{ fontFamily: fontData, fontSize: 11, fontWeight: 600, letterSpacing: "0.25em", color: "#d4ac6a", textTransform: "uppercase", marginBottom: 12 }}>
          Internal Reference · King's Academy Administration
        </div>
        <h1 style={{ fontFamily: fontDisplay, fontSize: "clamp(1.8rem,4vw,3rem)", fontWeight: 700, margin: "0 0 10px", lineHeight: 1.15 }}>
          Peer School <em style={{ color: "#d4ac6a" }}>Benchmarking</em>
        </h1>
        <p style={{ fontFamily: fontBody, fontStyle: "italic", color: "rgba(250,247,242,0.65)", fontSize: 15, margin: "0 0 20px" }}>
          King's Academy compared against peer institutions
        </p>

        {/* ── DATASET TOGGLE ── */}
        <div style={{ display: "inline-flex", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 3, overflow: "hidden" }}>
          {[
            { key: "gps", label: "Gateway to Prep Schools" },
            { key: "me",  label: "Middle East Schools" },
            { key: "all", label: "All Schools Combined" },
          ].map(({ key, label }) => (
            <button key={key}
              onClick={() => { setDatasetMode(key); setSearch(""); setStateFilter(""); setGenderFilter(""); setEsaOnly(false); setAllSubset("all"); }}
              style={{ padding: "8px 18px", border: "none", borderRight: key !== "all" ? "1px solid rgba(255,255,255,0.2)" : "none", background: datasetMode === key ? "#d4ac6a" : "transparent", color: datasetMode === key ? "#1a1714" : "rgba(250,247,242,0.7)", fontFamily: fontData, fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", cursor: "pointer", transition: "all 0.15s" }}>
              {label}
            </button>
          ))}
        </div>
      </header>

      {/* ── MIDDLE EAST DAY-SCHOOL WARNING ── */}
      {isMEMode && (
        <div style={{ background: "#5c3a1a", color: "#fdf0e0", padding: "12px 2rem", borderBottom: "2px solid #b8924a" }}>
          <div style={{ maxWidth: 1300, margin: "0 auto", display: "flex", gap: 12, alignItems: "flex-start" }}>
            <span style={{ fontSize: 18, flexShrink: 0 }}>⚠</span>
            <div style={{ fontFamily: fontData, fontSize: 12, lineHeight: 1.65 }}>
              <strong>Middle East schools are all day schools.</strong> For apples-to-apples comparison, use King's <em>day tuition ($42,000)</em> — not boarding tuition ($53,250). Financial aid is almost universally unpublished for ME schools; aid is typically employer- or government-sponsored, not need-based institutional grants. UAE and Qatar tuition figures are government-regulated (KHDA/ADEK) and highly reliable. Egyptian EGP figures are volatile and should be treated with extra caution.
            </div>
          </div>
        </div>
      )}

      {/* ── KING'S BANNER ── */}
      <section style={{ background: "#1a3a5c", color: "#fff", padding: "1.8rem 2rem" }}>
        <div style={{ maxWidth: 1300, margin: "0 auto", display: "flex", gap: 24, flexWrap: "wrap", alignItems: "flex-start" }}>
          <JordanFlag size={90} />
          <div style={{ flex: 1, minWidth: 240 }}>
            <div style={{ fontFamily: fontData, fontSize: 10, fontWeight: 600, letterSpacing: "0.22em", textTransform: "uppercase", color: "rgba(255,255,255,0.5)", marginBottom: 4 }}>King's Academy · Madaba, Jordan · Est. 2007</div>
            <h2 style={{ fontFamily: fontDisplay, fontSize: "1.5rem", fontWeight: 700, margin: "0 0 6px" }}>King's Academy</h2>
            <p style={{ fontSize: 13, color: "rgba(255,255,255,0.75)", lineHeight: 1.55, margin: "0 0 14px", maxWidth: 560 }}>
              Co-educational boarding and day school, grades 7–12, on a 144-acre campus in Madaba, Jordan. Founded by HM King Abdullah II, who attended Deerfield Academy. American AP-based curriculum; English instruction with required Arabic. NEASC accredited.
            </p>
            <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
              {[
                { lbl: "25-26 Boarding Tuition", val: "$53,250", conf: C.V },
                { lbl: "25-26 Day Tuition",      val: "$42,000", conf: C.V },
                { lbl: "% Boarding",             val: "55%",     conf: C.E },
                { lbl: "Nationalities",          val: "20+",     conf: C.V },
              ].map(({ lbl, val, conf }) => (
                <div key={lbl}>
                  <div style={{ fontFamily: fontData, fontSize: "1.3rem", fontWeight: 600, color: "#fff", lineHeight: 1, display: "flex", alignItems: "center", gap: 4 }}>
                    {val}
                    {conf !== C.V && <ConfDot conf={conf} />}
                  </div>
                  <div style={{ fontFamily: fontData, fontSize: 10, fontWeight: 500, letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(255,255,255,0.45)", marginTop: 2 }}>{lbl}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── CONFIDENCE LEGEND ── */}
      <div style={{ background: "#f4f0e8", borderBottom: "1px solid #e8e2d8", padding: "10px 2rem" }}>
        <div style={{ maxWidth: 1300, margin: "0 auto", display: "flex", gap: 20, flexWrap: "wrap", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center" }}>
            <span style={{ fontFamily: fontData, fontSize: 10, fontWeight: 600, letterSpacing: "0.15em", textTransform: "uppercase", color: "#7a7068" }}>Data confidence:</span>
            {Object.entries(CONF_STYLE).map(([k, s]) => <LegendItem key={k} confKey={k} style={s} />)}
            {isMEMode && <>
              <span style={{ fontFamily: fontData, fontSize: 10, fontWeight: 700, padding: "1px 6px", background: "#5c3a1a", color: "#fdf0e0", borderRadius: 1 }}>ME</span>
              <span style={{ fontFamily: fontData, fontSize: 10, color: "#7a7068" }}>= Middle East school</span>
            </>}
          </div>
          <span style={{ fontFamily: fontData, fontSize: 10, color: "#7a7068", fontStyle: "italic" }}>
            Hover any dot or label for details · Click charts to expand to full school list
          </span>
        </div>
      </div>

      {/* ── ANALYTICS ── */}
      <section style={{ background: "#f4f0e8", borderBottom: "3px double #c8bfb0", padding: "2.5rem 2rem" }}>
        <div style={{ maxWidth: 1300, margin: "0 auto" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12, marginBottom: 4 }}>
            <h2 style={{ fontFamily: fontDisplay, fontSize: "1.4rem", fontWeight: 600, margin: 0 }}>
              King's Academy vs. {datasetMode === "gps" ? "Gateway Peer Schools" : datasetMode === "me" ? "Middle East Schools" : "All Peer Schools"}
            </h2>
            <Link to="/chart-generator" style={{ fontFamily: fontData, fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", padding: "6px 14px", border: "1px solid #1a3a5c", borderRadius: 2, color: "#1a3a5c", textDecoration: "none", whiteSpace: "nowrap", flexShrink: 0, alignSelf: "center" }}>
              AI Chart Generator ↗
            </Link>
          </div>

          {/* ── ALL MODE SUBSET BUTTONS ── */}
          {datasetMode === "all" && (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 16, marginTop: 8 }}>
              <span style={{ fontFamily: fontData, fontSize: 10, fontWeight: 600, letterSpacing: "0.15em", textTransform: "uppercase", color: "#7a7068" }}>Show:</span>
              {[
                { key: "all", label: "All Schools" },
                { key: "gps", label: "GPS Only" },
                { key: "me",  label: "ME Only" },
                { key: "esa", label: "ESA Only" },
              ].map(({ key, label }) => (
                <button key={key} onClick={() => setAllSubset(key)}
                  style={{ padding: "4px 14px", border: `1px solid ${allSubset === key ? "#1a1714" : "#c8bfb0"}`, borderRadius: 2, background: allSubset === key ? "#1a1714" : "#faf7f2", color: allSubset === key ? "#faf7f2" : "#3d3830", fontFamily: fontData, fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", cursor: "pointer", transition: "all 0.15s" }}>
                  {label}
                </button>
              ))}
              <span style={{ fontFamily: fontData, fontSize: 11, color: "#7a7068", fontStyle: "italic" }}>King's Academy always included</span>
            </div>
          )}
          <p style={{ fontFamily: fontBody, fontStyle: "italic", color: "#7a7068", fontSize: 13, marginBottom: 20 }}>
            Selected comparisons. Click any chart to expand to all schools. Hover confidence dots for source details.
          </p>

          {/* Summary stat cards */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 12, marginBottom: 24 }}>
            {[
              { lbl: "King's Boarding Tuition", val: "$53K",  sub: "USD/yr",              kings: true },
              { lbl: "King's Day Tuition",      val: "$42K",  sub: "USD/yr",              kings: true },
              { lbl: "King's Aid Recipients",   val: "49%",   sub: "of students",         kings: true },
              { lbl: "King's Tuition:Aid Ratio",val: "3.7x",  sub: "gross tuition ÷ aid", kings: true },
            ].map(({ lbl, val, sub }) => (
              <div key={lbl} style={{ background: "#e8f0f8", border: "1px solid #1a3a5c", borderRadius: 2, padding: "14px 12px", textAlign: "center" }}>
                <div style={{ fontFamily: fontData, fontSize: 10, fontWeight: 600, letterSpacing: "0.16em", textTransform: "uppercase", color: "#1a3a5c", marginBottom: 6 }}>{lbl}</div>
                <div style={{ fontFamily: fontDisplay, fontSize: "1.8rem", fontWeight: 700, color: "#1a3a5c", lineHeight: 1 }}>{val}</div>
                <div style={{ fontFamily: fontData, fontSize: 11, color: "#7a7068", marginTop: 4 }}>{sub}</div>
              </div>
            ))}
          </div>

          {/* Chart grid — shown conditionally by mode */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))", gap: 20 }}>

            {/* Boarding Tuition — GPS and All modes only */}
            {datasetMode !== "me" && allTuition.length > 0 && (
              <ChartCard title="Boarding Tuition — Selected Peers (USD)"
                onExpand={() => setModal({ title: "Boarding Tuition — All Schools (USD)", description: CHART_DESCRIPTIONS.tuition, data: allTuition, maxVal: Math.max(...allTuition.map(x=>x.v)), colorFn: tuitionColor, unit: "$", suffix: "", filterOptions: modalFilters })}>
                {chartTuitionPreview.map(r => <HorzBar key={r.n} label={r.n} value={r.v} maxVal={Math.max(...chartTuitionPreview.map(x=>x.v))} color={tuitionColor(r)} isKings={r.k} unit="$" />)}
              </ChartCard>
            )}

            {/* Day Tuition — ME and All modes */}
            {isMEMode && allDayT.length > 0 && (
              <ChartCard title="Day Tuition — Selected Peers (USD)"
                onExpand={() => setModal({ title: "Day Tuition — All Schools (USD)", description: CHART_DESCRIPTIONS.dayTuition, data: allDayT, maxVal: Math.max(...allDayT.map(x=>x.v)), colorFn: dayTColor, unit: "$", suffix: "", filterOptions: modalFilters })}>
                {chartDayPreview.map(r => <HorzBar key={r.n} label={r.n} value={r.v} maxVal={Math.max(...chartDayPreview.map(x=>x.v))} color={dayTColor(r)} isKings={r.k} unit="$" />)}
              </ChartCard>
            )}

            {/* Financial Aid % — GPS and All modes (ME schools don't publish) */}
            {datasetMode !== "me" && allAid.length > 0 && (
              <ChartCard title="Financial Aid Recipients — % of Student Body"
                onExpand={() => setModal({ title: "Financial Aid Recipients — All Schools (%)", description: CHART_DESCRIPTIONS.aid, data: allAid, maxVal: Math.max(...allAid.map(x=>x.v)), colorFn: aidColor, suffix: "%", filterOptions: modalFilters })}>
                {chartAidPreview.map(r => <HorzBar key={r.n} label={r.n} value={r.v} maxVal={Math.max(...chartAidPreview.map(x=>x.v))} color={aidColor(r)} isKings={r.k} suffix="%" />)}
              </ChartCard>
            )}

            {/* Tuition:Aid Ratio — GPS and All modes */}
            {datasetMode !== "me" && allRatio.length > 0 && (
              <ChartCard title="Tuition:Aid Ratio — Lower = More Generous"
                onExpand={() => setModal({ title: "Tuition:Aid Ratio — All Schools", description: CHART_DESCRIPTIONS.ratio, data: allRatio, maxVal: Math.max(...allRatio.map(x=>x.v)), colorFn: ratioColor, filterOptions: modalFilters })}>
                {chartRatioPreview.map(r => <HorzBar key={r.n} label={r.n} value={r.v} maxVal={Math.max(...chartRatioPreview.map(x=>x.v))} color={ratioColor(r)} isKings={r.k} />)}
              </ChartCard>
            )}

            {/* Boarding Intensity — GPS and All modes */}
            {datasetMode !== "me" && allBoarding.length > 0 && (
              <ChartCard title="Boarding Intensity — % of Students Who Board"
                onExpand={() => setModal({ title: "Boarding Intensity — All Schools (%)", description: CHART_DESCRIPTIONS.boarding, data: allBoarding, maxVal: 100, colorFn: boardingColor, suffix: "%", filterOptions: modalFilters })}>
                {chartBoardingPreview.map(r => <HorzBar key={r.n} label={r.n} value={r.v} maxVal={100} color={boardingColor(r)} isKings={r.k} suffix="%" />)}
              </ChartCard>
            )}

            {/* Aid Sustainability — GPS and All modes */}
            {datasetMode !== "me" && allSustain.length > 0 && (
              <ChartCard title="Aid Sustainability — Aid Budget as % of Endowment"
                onExpand={() => setModal({ title: "Aid Sustainability — Aid Budget as % of Endowment", description: CHART_DESCRIPTIONS.sustainability, data: allSustain, maxVal: Math.max(...allSustain.map(x=>x.v)), colorFn: sustainColor, suffix: "%", noEndowmentSchools, filterOptions: modalFilters })}>
                <div style={{ fontFamily: "var(--font-data)", fontSize: 10, color: "#7a7068", marginBottom: 10, lineHeight: 1.5 }}>
                  Annual aid budget ÷ endowment. <strong>Lower is more sustainable.</strong> Green &lt;5% · Amber 5–12% · Red &gt;12%. Schools with no published endowment shown below.
                </div>
                {chartSustainPreview.map(r => <HorzBar key={r.n} label={r.n} value={r.v} maxVal={Math.max(...chartSustainPreview.map(x=>x.v))} color={sustainColor(r)} isKings={r.k} suffix="%" />)}
                <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid #e8e2d8" }}>
                  <div style={{ fontFamily: "var(--font-data)", fontSize: 10, fontWeight: 600, color: "#8b2c2c", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 5 }}>⚠ No published endowment</div>
                  {noEndowmentSchools.slice(0, 4).map(n => (
                    <div key={n} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                      <div style={{ width: 130, fontSize: 11, fontFamily: "var(--font-data)", color: n === "King's Academy" ? "#1a3a5c" : "#8b2c2c", fontWeight: n === "King's Academy" ? 700 : 400, flexShrink: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {n === "King's Academy" ? "★ " : ""}{n.replace("The ", "").replace(" School","").replace(" Academy","")}
                      </div>
                      <div style={{ flex: 1, background: "#fde8e8", borderRadius: 1, height: 14, display: "flex", alignItems: "center", paddingLeft: 6 }}>
                        <span style={{ fontFamily: "var(--font-data)", fontSize: 10, color: "#8b2c2c" }}>Aid funded from revenue only — no endowment backstop</span>
                      </div>
                    </div>
                  ))}
                  {noEndowmentSchools.length > 4 && (
                    <div style={{ fontFamily: "var(--font-data)", fontSize: 10, color: "#8b2c2c", marginTop: 3 }}>+{noEndowmentSchools.length - 4} more — click to expand</div>
                  )}
                </div>
              </ChartCard>
            )}
          </div>
        </div>
      </section>

      {/* ── DIRECTORY TABLE ── */}
      <section style={{ maxWidth: 1300, margin: "0 auto", padding: "2.5rem 2rem 4rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 12, borderBottom: "2px solid #1a1714", paddingBottom: 12, marginBottom: 16 }}>
          <div>
            <h2 style={{ fontFamily: fontDisplay, fontSize: "1.4rem", fontWeight: 600, margin: 0 }}>
              {esaOnly ? "Eight Schools Association (ESA) + King's Academy"
                : datasetMode === "gps" ? "Gateway to Prep Schools Directory"
                : datasetMode === "me" ? "Middle East Schools Directory"
                : "Combined Directory"}
            </h2>
            <div style={{ fontFamily: fontData, fontSize: 11, color: "#7a7068", letterSpacing: "0.08em", marginTop: 2 }}>
              Showing {filtered.length} of {activeSchools.length} schools · Click column headers to sort
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            {datasetMode === "gps" && (
              <button onClick={() => { setEsaOnly(v => !v); setSearch(""); setStateFilter(""); setGenderFilter(""); }}
                style={{ padding: "6px 14px", border: `1px solid ${esaOnly ? "#1a3a5c" : "#c8bfb0"}`, borderRadius: 1, background: esaOnly ? "#1a3a5c" : "#faf7f2", color: esaOnly ? "#fff" : "#3d3830", cursor: "pointer", fontSize: 12, fontWeight: 600, letterSpacing: "0.08em", transition: "all 0.15s", whiteSpace: "nowrap" }}>
                {esaOnly ? "✕ Show All Schools" : "ESA Schools Only"}
              </button>
            )}
            {!esaOnly && <>
              <input placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)} style={{ padding: "6px 10px", border: "1px solid #c8bfb0", background: "#faf7f2", color: "#1a1714", borderRadius: 1, minWidth: 160, fontSize: 13 }} />
              <select value={stateFilter} onChange={e => setStateFilter(e.target.value)} style={{ padding: "6px 8px", border: "1px solid #c8bfb0", background: "#faf7f2", color: "#1a1714", borderRadius: 1, fontSize: 12 }}>
                <option value="">All regions</option>
                {states.map(st => <option key={st} value={st}>{st === "JO" ? "JO — Jordan" : st === "BC" ? "BC — Canada" : st}</option>)}
              </select>
              <select value={genderFilter} onChange={e => setGenderFilter(e.target.value)} style={{ padding: "6px 8px", border: "1px solid #c8bfb0", background: "#faf7f2", color: "#1a1714", borderRadius: 1, fontSize: 12 }}>
                <option value="">All genders</option>
                <option value="Co-ed">Co-ed</option>
                <option value="Girls">Girls</option>
                <option value="Boys">Boys</option>
              </select>
            </>}
          </div>
        </div>

        {esaOnly && (
          <div style={{ background: "#e8f0f8", border: "1px solid #1a3a5c", borderRadius: 2, padding: "10px 14px", marginBottom: 16, fontFamily: fontData, fontSize: 12, color: "#1a3a5c", lineHeight: 1.6 }}>
            <strong>Eight Schools Association (ESA):</strong> Andover, Exeter, Choate, Deerfield, Hotchkiss, Lawrenceville, Northfield Mount Hermon, St. Paul's. Founded 1973; all eight commit to meeting 100% of demonstrated financial need. King's Academy shown alongside for direct comparison.
          </div>
        )}

        <div style={{ overflowX: "auto", border: "1px solid #c8bfb0", borderRadius: 2 }}>
          <table>
            <thead>
              <tr>
                <Th k="name">School</Th>
                <Th k="location">Location</Th>
                <Th k="founded">Est.</Th>
                <Th k="grades">Grades</Th>
                <Th k="enrollment">Enroll.</Th>
                <Th k="gender">Gender</Th>
                <Th k="boardingPct">% Board.</Th>
                <Th k="boardingT">Board. Tuition</Th>
                <Th k="dayT">Day Tuition</Th>
                <Th k="aidPct">% on Aid</Th>
                <Th k="aidBudgetM">Aid Budget</Th>
                <Th k="ratio">Tuition:Aid</Th>
                <Th k="endowmentM">Endowment</Th>
                <th style={{ fontFamily: fontData, fontSize: 10, fontWeight: 600, letterSpacing: "0.15em", textTransform: "uppercase", padding: "8px 8px", textAlign: "left", background: "#1a1714", color: "rgba(250,247,242,0.65)", whiteSpace: "nowrap" }}>Notes</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(s => {
                const isK   = s.isKings;
                const isEsa = ESA_NAMES.has(s.name);
                const isME  = !!s.isME;
                const rowBg = isK ? "#e8f0f8" : isME ? "#fdf5ec" : "transparent";
                const gStyle = s.gender === "Girls" ? { background: "#fef3e2", color: "#8b5a00" }
                  : s.gender === "Boys" ? { background: "#eaf4ec", color: "#1a5c2a" }
                  : { background: "#e8f0f8", color: "#1a3a5c" };
                return (
                  <tr key={s.name} style={{ background: rowBg, borderBottom: "1px solid #e8e2d8" }}>
                    <td style={{ padding: "7px 8px", minWidth: 165, fontFamily: fontDisplay, fontSize: 13, fontWeight: 400 }}>
                      <a href={s.url} target="_blank" rel="noopener noreferrer" style={{ color: isK ? "#1a3a5c" : "#1a1714", textDecoration: "none" }}>{s.name}</a>
                      {isK   && <span style={{ fontFamily: fontData, fontSize: 9, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", padding: "1px 4px", background: "#1a3a5c", color: "#fff", borderRadius: 1, marginLeft: 5, verticalAlign: "middle" }}>King's</span>}
                      {isEsa && <span style={{ fontFamily: fontData, fontSize: 9, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", padding: "1px 4px", background: "#3d3830", color: "#fff", borderRadius: 1, marginLeft: 3, verticalAlign: "middle" }}>ESA</span>}
                      {isME && !isK && <span style={{ fontFamily: fontData, fontSize: 9, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", padding: "1px 4px", background: "#5c3a1a", color: "#fdf0e0", borderRadius: 1, marginLeft: 3, verticalAlign: "middle" }}>ME</span>}
                      {s.isNew && <span style={{ fontFamily: fontData, fontSize: 9, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", padding: "1px 4px", background: "#22863a", color: "#fff", borderRadius: 1, marginLeft: 3, verticalAlign: "middle" }}>New</span>}
                    </td>
                    <td style={{ padding: "7px 8px", fontFamily: fontData, fontSize: 11, color: "#7a7068", whiteSpace: "nowrap" }}>{s.location}</td>
                    <td style={{ padding: "7px 8px", fontFamily: fontData, fontSize: 11, color: "#7a7068", textAlign: "right" }}>{s.founded}</td>
                    <td style={{ padding: "7px 8px", fontFamily: fontData, fontSize: 11, color: "#7a7068", whiteSpace: "nowrap" }}>{s.grades}</td>
                    <Cell field={s.enrollment} />
                    <td style={{ padding: "7px 8px" }}>
                      <span style={{ ...gStyle, fontFamily: fontData, fontSize: 10, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", padding: "2px 6px", borderRadius: 1 }}>{s.gender}</span>
                    </td>
                    <td style={{ padding: "7px 8px", minWidth: 80 }}>
                      <MiniBar pct={s.boardingPct.val} color={isK ? "#1a3a5c" : "#3d3830"} />
                    </td>
                    <MoneyCell field={s.boardingT} />
                    <td style={{ padding: "7px 8px", textAlign: "right", fontFamily: fontData, fontSize: 12, background: s.dayT.conf !== C.V ? CONF_STYLE[s.dayT.conf].bg : "transparent" }}>
                      {s.dayT.val
                        ? <span style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 2, color: s.dayT.conf !== C.V ? CONF_STYLE[s.dayT.conf].text : "inherit", fontWeight: 500 }}>${s.dayT.val.toLocaleString()}{s.dayT.conf !== C.V && <ConfDot conf={s.dayT.conf} />}</span>
                        : <span style={{ color: "#bbb", fontStyle: "italic", fontSize: 11 }}>N/A</span>}
                    </td>
                    <td style={{ padding: "7px 8px", minWidth: 80, background: s.aidPct.conf !== C.V ? CONF_STYLE[s.aidPct.conf].bg : "transparent" }}>
                      {s.aidPct.val !== null
                        ? <div style={{ display: "flex", alignItems: "center", gap: 4 }}><MiniBar pct={s.aidPct.val} color={isK ? "#1a3a5c" : "#8b2c2c"} />{s.aidPct.conf !== C.V && <ConfDot conf={s.aidPct.conf} />}</div>
                        : <span style={{ color: "#bbb", fontStyle: "italic", fontSize: 11 }}>N/A</span>}
                    </td>
                    <MoneyCell field={s.aidBudgetM} suffix="M" />
                    <RatioCell ratio={s.ratio} conf={s.ratioConf} />
                    <td style={{ padding: "7px 8px", textAlign: "right", fontFamily: fontData, fontSize: 11, color: "#3d3830", whiteSpace: "nowrap" }}>{s.endowmentText}</td>
                    <td style={{ padding: "7px 8px", fontSize: 11, color: "#7a7068", fontStyle: "italic", maxWidth: 200, lineHeight: 1.4 }}>{s.notes}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div style={{ marginTop: 14, fontFamily: fontData, fontSize: 11, color: "#7a7068", lineHeight: 1.8, letterSpacing: "0.03em" }}>
          <strong>Data notes:</strong> GPS tuition figures are 2024–25 where verified from school websites or the Ivy Talent Education 2024-25 published table. Wikipedia figures cite IRS Form 990 filings.
          ME tuition figures converted from local currencies at fixed-peg rates (AED ÷3.67, QAR ÷3.64, SAR ÷3.75, KWD ×3.25; EGP ÷50 approximate and volatile).
          Confidence: <span style={{ color: "#22863a", fontWeight: 600 }}>●</span> Verified primary source &nbsp;·&nbsp;
          <span style={{ color: "#d4a017", fontWeight: 600 }}>●</span> Estimated / secondary source &nbsp;·&nbsp;
          <span style={{ color: "#c0392b", fontWeight: 600 }}>●</span> Low confidence — do not cite without verification.
          Tuition:Aid ratio = estimated gross tuition revenue ÷ total aid disbursed. King's tuition at published USD rates. Not for external distribution.
        </div>
      </section>
    </div>
  );
}